import { AnnotationStatus } from "@faro-lotv/ielement-types";
import { CalendarIcon, ListViewIcon, NO_TRANSLATE_CLASS, TilesViewIcon } from "@flat-ui";
import { getMarkupStatusValue } from "@pages/project-details/project-markups/status/markup-status-utils";
import { LocalStorageUtils } from "@utils/local-storage-utils";
import { DateTime } from "luxon";

/** How the annotations of the project are shown */

/** Options to switch between the views of the annotations */
export const MARKUPS_VIEW_MODE_OPTIONS = [{
  label: "Table",
  value: "table",
  icon: <ListViewIcon />
}, {
  label: "Calendar",
  value: "calendar",
  icon: <CalendarIcon />
}, {
  label: "Board",
  value: "board",
  icon: <TilesViewIcon />
}];

/** Span of time shown in the annotations calendar */

/** Options to switch between the spans of time of the calendar */
export const MARKUPS_CALENDAR_VIEW_OPTIONS = [{
  label: "Week",
  value: "week"
}, {
  label: "Month",
  value: "month"
}];

/** Preferences of the annotations calendar that the user can change */

const DEFAULT_MARKUPS_CALENDAR_PREFERENCES = {
  shouldShowWeekends: true,
  shouldShowWeekNumbers: false,
  shouldUseLargeCards: false
};

/** Returns the calendar preferences stored in the local storage, or the defaults */
export function getStoredMarkupsCalendarPreferences() {
  const stored = LocalStorageUtils.getJSONItem("markups-calendar-preferences");
  const preferences = {
    ...DEFAULT_MARKUPS_CALENDAR_PREFERENCES
  };
  for (const key of Object.keys(preferences)) {
    const value = stored?.[key];
    if (typeof value === "boolean") {
      preferences[key] = value;
    }
  }
  return preferences;
}

/** Stores the calendar preferences in the local storage */
export function storeMarkupsCalendarPreferences(preferences) {
  LocalStorageUtils.setJSONItem("markups-calendar-preferences", preferences);
}

/** Returns the title of the calendar: the month and year, plus the ISO week number if requested */
export function getMarkupsCalendarTitle(visibleDate, calendarView, shouldShowWeekNumber) {
  const date = DateTime.fromJSDate(visibleDate);
  const title = date.toFormat("LLLL yyyy");
  return calendarView === "week" && shouldShowWeekNumber ? `${title} - Week ${date.weekNumber}` : title;
}

/** Returns the visible date moved by one week or month, backwards or forwards */
export function moveMarkupsCalendarDate(visibleDate, calendarView, direction) {
  return DateTime.fromJSDate(visibleDate).plus(calendarView === "week" ? {
    weeks: direction
  } : {
    months: direction
  }).toJSDate();
}

/** Class of the unclassified events, outlined to tell them apart from the open ones that share their color */
export const UNCLASSIFIED_EVENT_CLASS = "markups-calendar-unclassified";

/** Calendar palette of each status, matching the status colors of the rest of the tab */
const MARKUP_STATUS_EVENT_COLOR = {
  [AnnotationStatus.Unclassified]: "grey",
  [AnnotationStatus.Open]: "grey",
  [AnnotationStatus.InProgress]: "blue",
  [AnnotationStatus.ToReview]: "purple",
  [AnnotationStatus.Resolved]: "green"
};

/**
 * Returns the day of the due date of the markup, or undefined if it has no valid one.
 * Only the day matters, in the timezone of the user as in the table.
 */
function getMarkupDueDay(markup) {
  const dueDay = markup.dueDate ? DateTime.fromISO(markup.dueDate.value).startOf("day") : undefined;
  return dueDay?.isValid ? dueDay : undefined;
}

/**
 * Returns an all-day calendar event on the due date of every markup that has one.
 * The events get the class of the avatar of their assignee, if provided.
 */
export function getMarkupsCalendarEvents(markups, assigneeClasses = new Map()) {
  return markups.flatMap(markup => {
    // The calendar throws on invalid dates, so markups without a valid due date are skipped
    const dueDay = getMarkupDueDay(markup)?.toISODate();
    if (!dueDay) {
      return [];
    }
    const status = getMarkupStatusValue(markup.status?.value);
    const start = `${dueDay}T00:00:00`;
    return [{
      id: markup.id,
      title: markup.name,
      start,
      end: start,
      // eslint-disable-next-line @typescript-eslint/naming-convention -- attribute from another lib
      allDay: true,
      color: MARKUP_STATUS_EVENT_COLOR[status],
      className: [NO_TRANSLATE_CLASS, status === AnnotationStatus.Unclassified ? UNCLASSIFIED_EVENT_CLASS : undefined, assigneeClasses.get(markup.id)].filter(Boolean).join(" ")
    }];
  });
}

/** Number of Saturday in Luxon, which numbers the weekdays from 1 on Monday to 7 on Sunday */
const SATURDAY = 6;
/**
 * Returns the markups due on a day shown in the calendar, in the order of their due day as the
 * calendar shows them. The month view also shows the days of the previous and next months that
 * complete its first and last weeks, which start on Monday.
 */
export function getMarkupsShownInCalendar(markups, {
  visibleDate,
  calendarView,
  shouldShowWeekends
}) {
  const date = DateTime.fromJSDate(visibleDate);
  const firstDay = date.startOf(calendarView).startOf("week");
  const lastDay = date.endOf(calendarView).endOf("week");
  const shown = markups.flatMap(markup => {
    const dueDay = getMarkupDueDay(markup);
    const isShown = !!dueDay && dueDay >= firstDay && dueDay <= lastDay && (shouldShowWeekends || dueDay.weekday < SATURDAY);
    return isShown ? [{
      markup,
      dueDay: dueDay.toMillis()
    }] : [];
  });

  // Stable, as the calendar keeps the given order of the events of a day
  return shown.sort((a, b) => a.dueDay - b.dueDay).map(({
    markup
  }) => markup);
}